import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import * as Clipboard from 'expo-clipboard';
import * as Linking from 'expo-linking';
import {
  BodyText,
  Button,
  FadeView,
  HStack,
  LocationCard,
  ModalCloseBtn,
  theme,
  VStack,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useReorderState } from '../../../ReorderingScreen/hooks';
import type { LocationDetailsProps } from '../../LocationsScreen.types';
import {
  getLocationA11yProps,
  getUserDistance,
} from '../../LocationsScreen.utils';
import { LocationDetailsPill } from './SingleLocation.pill';

export const LocationDetailsMobile = React.memo(
  (props: LocationDetailsProps) => {
    const {
      location,
      userLocation,
      onCardPress,
      handleOnBack,
      handleOnCallToAction,
    } = props;
    const [showFeedback, setShowFeedback] = React.useState(false);
    const { reordering, restaurantSlug } = useReorderState();
    const { t } = useLocalizationContext();
    const viewMenuText = t('location.order-now');
    const { bottom, top } = useSafeAreaInsets();
    const marginBottom = theme.spacing.medium + bottom + top;
    const footerMargin = useStyle(() => ({ marginBottom }), [marginBottom]);
    const a11yProps = getLocationA11yProps(t, location, 'button');

    // ─────────────────────── Callbacks ──────────────────────────────────
    const handleOnCall = React.useCallback(() => {
      void Linking.openURL(`tel:${location.phone}`);
    }, [location]);
    const handleOnDirections = React.useCallback(() => {
      const url = Platform.select({
        web: `geo:0,0?q=${location.address}`,
        ios: `maps:0,0?q=${location.address}`,
        android: `geo:0,0?q=${location.address}`,
      });

      void Linking.openURL(url ?? '');
    }, [location]);
    const handleOnUber = React.useCallback(() => {
      void Linking.openURL(
        `https://m.uber.com/ul/?action=setPickup&client_id=sweetgreen&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(
          location.address,
        )}&dropoff[latitude]=${location.lat}&dropoff[longitude]=${
          location.lng
        }`,
      );
    }, [location]);
    const handleOnCopy = React.useCallback(() => {
      void Clipboard.setStringAsync(location.address);
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
      }, 3000);
    }, [location]);

    return (
      <>
        <VStack style={styles.content} testID="location.details">
          <View style={styles.header}>
            <BodyText style={styles.title} size={2}>
              {t('location.details.title')}
            </BodyText>
            <ModalCloseBtn style={styles.closeButton} onPress={handleOnBack} />
          </View>
          <LocationCard
            key={location?.id}
            isLoading={
              reordering
                ? restaurantSlug === location.restaurantSlug
                : undefined
            }
            distance={getUserDistance(userLocation.geolocation, location)}
            onCardPress={onCardPress}
            {...a11yProps}
            {...location}
          />
          <HStack style={styles.buttons}>
            {location.phone ? (
              <LocationDetailsPill
                id="call"
                title={t('location.details.call')}
                icon="IconPhone"
                onPress={handleOnCall}
              />
            ) : null}
            <LocationDetailsPill
              id="map"
              title={t('location.details.directions')}
              icon="IconMap"
              onPress={handleOnDirections}
            />
            <LocationDetailsPill
              id="uber"
              title={t('location.details.uber')}
              icon="IconCar"
              onPress={handleOnUber}
            />
            <LocationDetailsPill
              id="copy"
              title={t('location.details.copy')}
              icon="IconShare2"
              onPress={handleOnCopy}
            />
          </HStack>
        </VStack>
        <View style={[styles.footer, footerMargin]}>
          <FadeView show={showFeedback} style={styles.feedback}>
            <BodyText>{t('location.details.feedback')}</BodyText>
          </FadeView>
          <View style={styles.button}>
            <Button
              size="large"
              testID="locationsearch.view-menu"
              onPress={handleOnCallToAction}
            >
              {viewMenuText}
            </Button>
          </View>
        </View>
      </>
    );
  },
);

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: theme.spacing.medium,
    position: 'relative',
  },
  header: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  title: {
    textAlign: 'center',
  },
  footer: {
    marginTop: 'auto',
  },
  feedback: {
    borderStyle: 'solid',
    borderBottomColor: theme.colors.DARK_KALE,
    borderBottomWidth: 1,
    borderTopColor: theme.colors.DARK_KALE,
    borderTopWidth: 1,
    backgroundColor: theme.colors.CUCUMBER,
    padding: theme.spacing.medium,
  },
  buttons: {
    flexWrap: 'wrap',
  },
  button: {
    marginTop: theme.spacing.medium,
    paddingHorizontal: theme.spacing.medium,
  },
});
